import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getCookies } from 'cookies-next';
import { config } from '../../data/config';
import { useTranslation } from 'react-i18next';
import { Input, Button } from 'rsuite';

interface GiftProduct {
    _id: string;
    id: string;
    name: {
        ku: string;
        fa: string;
        en: string;
        ar: string;
        tr: string;
    } | string;
}

interface GiftProductSelectorProps {
    selectedGiftProducts: string[];
    onSelectionChange: (giftProductIds: string[]) => void;
    activeLang: string;
}

export default function GiftProductSelector({ 
    selectedGiftProducts, 
    onSelectionChange, 
    activeLang 
}: GiftProductSelectorProps) {
    const { t } = useTranslation();
    const cookie = getCookies();
    const [giftProducts, setGiftProducts] = useState<GiftProduct[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Helper function to get display name from multilingual or string
    const getDisplayName = (name: any, lang: string = activeLang): string => {
        if (!name) return '';
        if (typeof name === 'string') return name;
        if (typeof name === 'object') {
            return name[lang] || name.en || name.fa || name.ku || name.ar || name.tr || '';
        }
        return String(name);
    };

    useEffect(() => {
        loadGiftProducts();
    }, []);

    const loadGiftProducts = async () => {
        setLoading(true);
        try {
            const res = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/giftproducts/all`,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });
            // Handle both paginated and non-paginated responses
            let products = res.data.message || [];
            if (products.docs) {
                products = products.docs;
            }
            
            // Normalize multilingual names and ensure IDs are strings
            const normalizedProducts = products.map((product: any) => {
                // Normalize ID to string
                const productId = String(product._id || product.id || '');
                
                // Normalize name
                let normalizedName = product.name;
                if (product.name && typeof product.name === 'string') {
                    normalizedName = {
                        ku: product.name,
                        fa: product.name,
                        en: product.name,
                        ar: product.name,
                        tr: product.name
                    };
                } else if (product.name && product.name.nl) {
                    normalizedName = {
                        ...product.name,
                        ku: product.name.nl,
                    };
                    delete normalizedName.nl;
                    if (!normalizedName.tr) normalizedName.tr = '';
                }
                
                return {
                    ...product,
                    _id: productId,
                    id: productId,
                    name: normalizedName
                };
            });
            setGiftProducts(normalizedProducts);
        } catch (error: any) {
            console.error('Error loading gift products:', error);
        } finally {
            setLoading(false);
        }
    };

    // Helper to normalize ID for comparison
    const normalizeId = (id: any): string => {
        return String(id || '').trim();
    };

    const toggleGiftProduct = (giftProductId: string) => {
        const normalizedId = normalizeId(giftProductId);
        const normalizedSelected = selectedGiftProducts.map(normalizeId);
        const isSelected = normalizedSelected.includes(normalizedId);
        
        if (isSelected) {
            onSelectionChange(selectedGiftProducts.filter(id => normalizeId(id) !== normalizedId));
        } else {
            onSelectionChange([...selectedGiftProducts, normalizedId]);
        }
    };

    const filteredGiftProducts = giftProducts.filter((product) => {
        const displayName = getDisplayName(product.name, activeLang).toLowerCase();
        return displayName.includes(searchTerm.toLowerCase());
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4" dir="rtl">
            {/* Search Bar */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    جستجوی محصول هدیه / Search Gift Products
                </label>
                <Input
                    placeholder="جستجو..."
                    value={searchTerm}
                    onChange={(value) => setSearchTerm(value)}
                    className="w-full"
                />
            </div>

            {/* Gift Products List */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-800">
                        محصولات هدیه / Gift Products
                    </h3>
                    <span className="text-xs text-gray-500">
                        {selectedGiftProducts.length} انتخاب شده
                    </span>
                </div>

                {filteredGiftProducts.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {filteredGiftProducts.map((product) => {
                            const productId = normalizeId(product._id || product.id);
                            const normalizedSelected = selectedGiftProducts.map(normalizeId);
                            const isSelected = normalizedSelected.includes(productId);
                            
                            return (
                                <div
                                    key={productId}
                                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
                                        isSelected
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                    onClick={() => toggleGiftProduct(productId)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleGiftProduct(productId)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-700">
                                            {getDisplayName(product.name, activeLang)}
                                        </p>
                                    </div>
                                    {isSelected && (
                                        <span className="text-blue-600 text-xs font-semibold">✓ انتخاب شده</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        {searchTerm ? (
                            <p>هیچ محصول هدیه‌ای با این نام یافت نشد</p>
                        ) : (
                            <p>هیچ محصول هدیه‌ای تعریف نشده است</p>
                        )}
                    </div>
                )}
            </div>

            {/* Selected Count */}
            {selectedGiftProducts.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                        <strong>{selectedGiftProducts.length}</strong> محصول هدیه انتخاب شده است
                    </p>
                </div>
            )}
        </div>
    );
}

